/**
 * Transformaciones de listas para las páginas: búsqueda de partidos y
 * rankings de temporada. Puro y testeable (las páginas sólo pintan).
 */
import type { MatchSummary, SkillLine } from '@scout/core';
import type { SeasonTeamReport } from './api-client';
import { playerLabel } from './format';

/** Búsqueda por texto sobre nombre, id y reglamento. */
export function filterMatches(matches: readonly MatchSummary[], query: string): MatchSummary[] {
  const q = query.trim().toLowerCase();
  if (q === '') return [...matches];
  return matches.filter(
    (m) =>
      m.name.toLowerCase().includes(q) ||
      m.matchId.toLowerCase().includes(q) ||
      m.sportConfigId.toLowerCase().includes(q),
  );
}

/** Fila de un ranking simple. */
export interface RankRow {
  key: string;
  team: string;
  player: string;
  value: number;
  /** Volumen de la muestra (ataques o partidos), para desempatar y mostrar. */
  volume: number;
}

/** Mínimo de ataques para entrar al ranking de eficiencia. */
export const MIN_ATTACKS_FOR_RANK = 20;

/**
 * Ranking por eficiencia de ataque con piso de volumen: sin mínimo, el que
 * remató una vez y la clavó lidera con 100% y el ranking no dice nada.
 */
export function rankByAttackEfficiency(
  teams: readonly SeasonTeamReport[],
  minAttacks = MIN_ATTACKS_FOR_RANK,
): RankRow[] {
  const rows: RankRow[] = [];
  for (const team of teams) {
    for (const player of team.players) {
      const attack: SkillLine | undefined = player.skills['attack'];
      if (!attack || attack.total < minAttacks) continue;
      rows.push({
        key: `${team.name}-${player.number}-${player.name ?? ''}`,
        team: team.name,
        player: playerLabel(player.number, player.name),
        value: attack.efficiency,
        volume: attack.total,
      });
    }
  }
  return rows.sort((a, b) => b.value - a.value || b.volume - a.volume);
}

/** Ranking por puntos anotados (sin mínimo: los puntos son puntos). */
export function rankByPoints(teams: readonly SeasonTeamReport[]): RankRow[] {
  const rows: RankRow[] = [];
  for (const team of teams) {
    for (const player of team.players) {
      if (player.pointsScored <= 0) continue;
      rows.push({
        key: `${team.name}-${player.number}-${player.name ?? ''}`,
        team: team.name,
        player: playerLabel(player.number, player.name),
        value: player.pointsScored,
        volume: player.matches,
      });
    }
  }
  return rows.sort((a, b) => b.value - a.value || a.volume - b.volume);
}
